import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import PedigreeTabs from "../components/PedigreeTabs.jsx";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import { ArrowLeft, Edit, Send, Trash2, Tag, ShieldAlert } from "lucide-react";

const HORSE_PLACEHOLDER = "https://images.pexels.com/photos/32076745/pexels-photo-32076745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Row = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-[#E5DFD3] last:border-0">
      <div className="w-1/3 text-xs uppercase tracking-wider text-[#5C4F42] font-bold">{label}</div>
      <div className="flex-1 text-sm text-[#2C241B]">{value}</div>
    </div>
  );
};

const HorseProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [horse, setHorse] = useState(null);
  const [tree, setTree] = useState(null);
  const [progenyData, setProgenyData] = useState(null);
  const [offspring, setOffspring] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ name: "", text: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [h, p, prog, o, c] = await Promise.all([
        api.get(`/horses/${id}`),
        api.get(`/horses/${id}/pedigree?depth=6`),
        api.get(`/horses/${id}/progeny?depth=4`),
        api.get(`/horses/${id}/offspring`),
        api.get(`/horses/${id}/comments`),
      ]);
      setHorse(h.data);
      setTree(p.data);
      setProgenyData(prog.data);
      setOffspring(o.data);
      setComments(c.data);
    } catch {
      setError("Адуу олдсонгүй");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setError("");
    loadData();
  }, [id, loadData]);

  const postComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.text.trim()) {
      toast.error("Нэр болон сэтгэгдэл шаардлагатай");
      return;
    }
    try {
      await api.post(`/horses/${id}/comments`, commentForm);
      toast.success("Сэтгэгдэл нэмэгдлээ");
      setCommentForm({ name: "", text: "" });
      const { data } = await api.get(`/horses/${id}/comments`);
      setComments(data);
    } catch {
      toast.error("Илгээхэд алдаа гарлаа");
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Сэтгэгдлийг устгах уу?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      setComments((cs) => cs.filter((c) => c.id !== cid));
      toast.success("Устгагдлаа");
    } catch {
      toast.error("Устгах үед алдаа гарлаа");
    }
  };

  if (loading) return <div className="py-24 text-center text-xl text-[#5C4F42]">Ачааллаж байна...</div>;
  if (error || !horse) return (
    <div className="py-24 text-center">
      <div className="text-2xl text-[#1B3B5A] font-bold mb-2">{error}</div>
      <Link to="/" className="text-[#B8860B] hover:underline">Нүүр рүү буцах</Link>
    </div>
  );

  const photos = (horse.photo_paths && horse.photo_paths.length > 0)
    ? horse.photo_paths
    : (horse.photo_path ? [horse.photo_path] : []);
  const mainPhoto = photos.length > 0 ? fileUrl(photos[activePhotoIdx] || photos[0]) : HORSE_PLACEHOLDER;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[#1B3B5A] hover:text-[#B8860B]" data-testid="back-btn">
          <ArrowLeft size={18} strokeWidth={1.5} /> Буцах
        </button>
        {admin && (
          <Link to={`/admin/edit/${horse.id}`} className="inline-flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 rounded hover:bg-[#996F09] text-sm font-bold transition-colors" data-testid="edit-horse-btn">
            <Edit size={16} strokeWidth={1.5} /> Засах
          </Link>
        )}
      </div>

      {/* Header */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <div className="aspect-[4/3] rounded-lg overflow-hidden border border-[#D6CFC4] shadow bg-[#F0EBE1]">
            <img src={mainPhoto} alt={horse.name} className="w-full h-full object-cover" data-testid="horse-photo" />
          </div>
          {photos.length > 1 && (
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid="horse-gallery">
              {photos.map((p, idx) => (
                <button
                  key={p + idx}
                  type="button"
                  onClick={() => setActivePhotoIdx(idx)}
                  className={`aspect-square rounded overflow-hidden border-2 transition-colors ${idx === activePhotoIdx ? "border-[#B8860B]" : "border-[#D6CFC4] hover:border-[#1B3B5A]"}`}
                  data-testid={`gallery-thumb-${idx}`}
                >
                  <img src={fileUrl(p)} alt={`${horse.name} ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {horse.is_for_sale && (
            <div className="mt-3 bg-[#B8860B] text-white rounded p-3 flex items-center gap-2" data-testid="for-sale-banner">
              <Tag size={18} />
              <div className="flex-1">
                <div className="font-bold text-sm">Зарагдаж байна</div>
                <div className="text-xs opacity-90">{[horse.sale_price, horse.sale_contact].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-2 font-bold">
            {[horse.gender, horse.color].filter(Boolean).join(" · ") || "Адуу"}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1B3B5A] leading-tight" data-testid="horse-name">
            {horse.name}
          </h1>
          <div className="mt-5 bg-white border border-[#D6CFC4] rounded-lg p-5">
            <Row label="Төрсөн он" value={horse.birth_year} />
            <Row label="Зүс" value={horse.color} />
            <Row label="Хүйс" value={horse.gender} />
            <Row label="Микрочип" value={horse.microchip && <span className="font-mono">{horse.microchip}</span>} />
            <Row label="Тамга" value={horse.brand_stamp} />
            <Row label="Бүртгэлийн дугаар" value={horse.registration_number && <span className="font-bold">{horse.registration_number}</span>} />
            <Row label="Улс" value={horse.country} />
            <Row label="Эцэг" value={horse.father && (
              <Link to={`/horse/${horse.father.id}`} className="text-[#1B3B5A] hover:text-[#B8860B] font-bold">{horse.father.name}</Link>
            )} />
            <Row label="Эх" value={horse.mother && (
              <Link to={`/horse/${horse.mother.id}`} className="text-[#1B3B5A] hover:text-[#B8860B] font-bold">{horse.mother.name}</Link>
            )} />
            <Row label="Эзэмшигч" value={horse.owner_name} />
            <Row label="Үржүүлэгч (Аж ахуй)" value={horse.breeder_farm} />
          </div>
          {horse.description && (
            <div className="mt-4 bg-[#F0EBE1]/60 border border-[#D6CFC4] rounded p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-[#5C4F42] mb-1 font-bold">Нэмэлт мэдээлэл</div>
              <div className="text-sm text-[#2C241B] whitespace-pre-line">{horse.description}</div>
            </div>
          )}
          {horse.achievements && (
            <div className="mt-4 bg-[#FEF7E0] border-l-4 border-[#B8860B] rounded-r p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] mb-1 font-bold">Үзүүлсэн амжилт</div>
              <div className="text-sm text-[#2C241B] whitespace-pre-line" data-testid="horse-achievements">{horse.achievements}</div>
            </div>
          )}
        </div>
      </section>

      {/* Pedigree + Progeny Tabs */}
      <section className="mt-14">
        <div className="ornament-divider mb-6">✦</div>
        <div className="bg-white border border-[#D6CFC4] rounded-lg shadow-sm p-4 md:p-6">
          <PedigreeTabs horse={horse} pedigreeTree={tree} progenyTree={progenyData} />
        </div>
      </section>

      {/* Offspring */}
      {offspring.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-[#1B3B5A] mb-4">Үр удам ({offspring.length})</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {offspring.map((o) => (
              <Link
                key={o.id}
                to={`/horse/${o.id}`}
                className="bg-white border border-[#D6CFC4] rounded p-2 text-center hover:border-[#B8860B] transition-colors"
                data-testid={`offspring-${o.id}`}
              >
                <img src={o.photo_path ? fileUrl(o.photo_path) : HORSE_PLACEHOLDER} alt={o.name} className="w-full aspect-square object-cover rounded mb-2" />
                <div className="text-sm text-[#1B3B5A] font-bold truncate">{o.name}</div>
                <div className="text-[10px] text-[#5C4F42]">{o.birth_year}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Comments */}
      <section className="mt-14">
        <h2 className="text-2xl font-bold text-[#1B3B5A] mb-4">Сэтгэгдэл ({comments.length})</h2>
        <form onSubmit={postComment} className="bg-white border border-[#D6CFC4] rounded-lg p-4 mb-5 space-y-3" data-testid="comment-form">
          <input
            value={commentForm.name}
            onChange={(e) => setCommentForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Таны нэр"
            className="w-full border border-[#D6CFC4] rounded px-3 py-2 focus:outline-none focus:border-[#B8860B]"
            data-testid="comment-name"
            required
          />
          <textarea
            value={commentForm.text}
            onChange={(e) => setCommentForm((f) => ({ ...f, text: e.target.value }))}
            placeholder="Сэтгэгдэлээ бичих..."
            rows={3}
            className="w-full border border-[#D6CFC4] rounded px-3 py-2 focus:outline-none focus:border-[#B8860B]"
            data-testid="comment-text"
            required
          />
          <button type="submit" className="inline-flex items-center gap-2 bg-[#1B3B5A] text-white hover:bg-[#264F78] px-4 py-2 rounded font-bold transition-colors text-sm" data-testid="comment-submit">
            <Send size={14} /> Илгээх
          </button>
        </form>
        {comments.length === 0 ? (
          <div className="text-center text-[#5C4F42] py-6 text-sm">Одоогоор сэтгэгдэл алга. Анх сэтгэгдэл үлдээгээрэй.</div>
        ) : (
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="bg-white border border-[#D6CFC4] rounded p-3" data-testid={`comment-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[#1B3B5A]">{c.name}</div>
                    <div className="text-xs text-[#5C4F42]">{new Date(c.created_at).toLocaleString("mn-MN")}</div>
                    <div className="text-sm text-[#2C241B] mt-1 whitespace-pre-line">{c.text}</div>
                  </div>
                  {admin && (
                    <button onClick={() => deleteComment(c.id)} className="text-[#8B2500] hover:bg-[#8B2500]/10 p-1.5 rounded" data-testid={`comment-delete-${c.id}`} title="Устгах">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!admin && (
          <div className="mt-3 text-xs text-[#5C4F42] flex items-center gap-1">
            <ShieldAlert size={12} /> Зохисгүй сэтгэгдлүүдийг админ шалгаж устгадаг.
          </div>
        )}
      </section>
    </div>
  );
};

export default HorseProfile;
